import axios from 'axios';
import { getFirestore, collection, addDoc, getDocs, query, orderBy, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { dataApp } from '../config/firebase';
import { IdeogramPrompt, Style, AspectRatio } from '../components/ideogram/types';

// Use Netlify functions with correct URLs for each environment
const API_BASE_URL = process.env.NODE_ENV === 'development'
  ? 'http://localhost:8888/.netlify/functions'
  : 'https://whale-app-b8vv4.ondigitalocean.app/.netlify/functions';

export interface IdeogramGenerateRequest {
  prompt: string;
  style?: string;
  aspect_ratio?: string;
  cfg_scale?: number;
  seed?: number;
  negative_prompt?: string;
  steps?: number;
  source_image_url?: string;
  image_weight?: number;
}

export interface IdeogramGenerateResponse {
  created: string;
  data: Array<{
    is_image_safe: boolean;
    prompt: string;
    resolution: string;
    seed: number;
    style_type: string;
    url: string;
    firebaseUrl: string;
  }>;
}

export interface GeneratePromptsRequest {
  seedPrompt: string;
  promptStyle: string;
  numberOfPrompts: number;
}

export interface GeneratePromptsResponse {
  prompts: string[];
}

// Create an axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  },
  timeout: 60000, // 60 second timeout since image generation can take time
  withCredentials: true // Required for CORS with credentials
});

// Add auth token interceptor
apiClient.interceptors.request.use(async (config) => {
  try {
    const auth = getAuth();
    const user = auth.currentUser;
    if (user) {
      const token = await user.getIdToken();
      config.headers.Authorization = `Bearer ${token}`;
    }
  } catch (error) {
    
  }
  return config;
});

interface GenerateImageOptions {
  sourceImageUrl?: string;
  imageWeight?: number;
}

export const generateImageAsync = async (
  prompt: string,
  aspectRatio: AspectRatio,
  style: Style,
  options?: GenerateImageOptions
): Promise<string> => {
  try {
    
    
    // Create a placeholder document in Firestore first
    const db = getFirestore(dataApp);
    const promptDoc = {
      style,
      aspectRatio,
      prompt,
      imageUrl: '',  // Will be updated when image is ready
      createdAt: new Date().toISOString(),
      status: 'generating',
      ...(options?.sourceImageUrl ? { sourceImageUrl: options.sourceImageUrl } : {}),
      isRemix: !!options?.sourceImageUrl
    };

    const docRef = await addDoc(collection(db, 'ideogram-prompts'), promptDoc);
    

    // Start image generation in background
    apiClient.post<IdeogramGenerateResponse>(
      '/ideogram-generate',
      {
        prompt,
        aspect_ratio: aspectRatio,
        style: style,
        ...(options?.sourceImageUrl ? {
          source_image_url: options.sourceImageUrl,
          image_weight: options.imageWeight
        } : {})
      }
    ).then(async (response) => {
      

      if (!response.data?.data?.[0]?.firebaseUrl) {
        throw new Error('No Firebase URL in response');
      }

      // Update the Firestore document with the image URL
      await updateDoc(doc(db, 'ideogram-prompts', docRef.id), {
        imageUrl: response.data.data[0].firebaseUrl,
        status: 'completed'
      });
      
      
    }).catch(async (error) => {
      
      
      
      // Update document status to error
      await updateDoc(doc(db, 'ideogram-prompts', docRef.id), {
        status: 'error',
        error: error.message || 'Unknown error occurred'
      });
    });

    return docRef.id;
  } catch (error) {
    
    throw error;
  }
};

export const describeImage = async (imageFile: File | null, imageUrl: string | null): Promise<string> => {
  if (!imageFile && !imageUrl) {
    throw new Error('Either image file or URL must be provided');
  }

  try {
    let requestData: { imageUrl?: string; imageData?: string } = {};

    if (imageFile) {
      // Convert File to base64 data URL
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target?.result as string);
        reader.onerror = (e) => reject(e);
        reader.readAsDataURL(imageFile);
      });
      requestData.imageData = dataUrl;
    } else if (imageUrl) {
      requestData.imageUrl = imageUrl;
    }

    const response = await apiClient.post('/ideogram-describe', requestData);

    if (!response.data?.descriptions?.[0]?.text) {
      throw new Error('Invalid response format from API');
    }

    return response.data.descriptions[0].text;
  } catch (error) {
    
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    throw error;
  }
};

export const deletePrompt = async (promptId: string): Promise<void> => {
  try {
    const db = getFirestore(dataApp);
    await deleteDoc(doc(db, 'ideogram-prompts', promptId));
  } catch (error) {
    
    throw new Error('Failed to delete prompt');
  }
};

export const getPrompts = async (): Promise<{ prompts: IdeogramPrompt[]; totalImages: number; lastActivity: string }> => {
  try {
    const db = getFirestore(dataApp);
    const promptsRef = collection(db, 'ideogram-prompts');
    const q = query(promptsRef, orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    
    const prompts: IdeogramPrompt[] = [];
    querySnapshot.forEach((doc) => {
      prompts.push({
        id: doc.id,
        ...doc.data()
      } as IdeogramPrompt);
    });

    const totalImages = prompts.length;
    const lastActivity = prompts.length > 0 ? prompts[0].createdAt : '-';

    return { prompts, totalImages, lastActivity };
  } catch (error) {
    
    throw error;
  }
};

export const generatePrompts = async (request: GeneratePromptsRequest): Promise<GeneratePromptsResponse> => {
  try {
    const response = await apiClient.post('/generate-prompts', {
      prompt: request.seedPrompt,
      style: request.promptStyle,
      numberOfPrompts: request.numberOfPrompts,
    });

    return response.data;
  } catch (error) {
    
    throw error;
  }
};
